<template>
  <div class="wrapper">
    <div class="controls">
      <a-input v-model:value="search" placeholder="Buscar..." class="search" @input="handleSearch">
        <template #prefix>
          <SearchOutlined />
        </template>
      </a-input>

      <entity-picker
        v-if="user?.customer?.type_business == 1"
        :value="entity"
        @update:value="entity = $event"
      />

      <campaign-picker
        :showAll="true"
        :value="campaign"
        @update:value="campaign = $event"
        :entity="entity"
      />

      <QueuePicker
        v-show="windowWidth >= 1300"
        :key="'queue-outer-' + campaign"
        :value="queue"
        @update:value="queue = $event"
        :portfolio="campaign"
        :entity="entity"
        :showAll="true"
        :withOutPortfolio="false"
      >
        <template #no-queues-content>
          <div style="text-align: left; font-size: 14px;">
            <p style="margin-bottom: 12px; color: #434343;">
              La campaña seleccionada<br>no cuenta con colas
            </p>
            <p style="margin-bottom: 0; color: #8c8c8c;">
              Para crear una, dirígete a la<br>sección <router-link to="/operations/queues" style="color: #1890ff;">Colas.</router-link>
            </p>
          </div>
        </template>
      </QueuePicker>


      <a-popover
        placement="bottomRight"
        trigger="click"
        overlayClassName="filters-popover"
        :forceRender="true"
        :getPopupContainer="(triggerNode) => triggerNode.parentNode"
      >
        <template #content>
          <div class="filters-popover-content" ref="filtersPopoverContent">
            <div class="filters-header">
              <span class="filters-title">Filtros adicionales</span>
              <a-tooltip title="Se restablecerán todos los filtros a su configuración por defecto.">
                <span>
                  <a-button type="link" class="filters-reset" @click="resetFilters" :disabled="!hasActiveFilters">Restablecer</a-button>
                </span>
              </a-tooltip>
            </div>
            <a-divider style="margin: 0" />
            


            <div class="filter-item" v-show="windowWidth < 1300">
              <QueuePicker
                :key="'queue-inner-' + campaign"
                :value="queue"
                @update:value="queue = $event"
                :portfolio="campaign"
                :entity="entity"
                :showAll="true"
                :withOutPortfolio="false"
              >
                <template #no-queues-content>
                  <div style="text-align: left; font-size: 14px;">
                    <p style="margin-bottom: 12px; color: #434343;">
                      <strong>La campaña seleccionada<br>no cuenta con colas</strong>
                    </p>
                    <p style="margin-bottom: 0; color: #8c8c8c;">
                      Para crear una, dirígete a la<br>sección <router-link to="/operations/queues" style="color: #1890ff;">Colas.</router-link>
                    </p>
                  </div>
                </template>
              </QueuePicker>
            </div>

            <div class="filter-item">
              <a-select
                :getPopupContainer="(triggerNode) => triggerNode.parentNode"
                v-model:value="selected"
                class="form__select"
                placeholder="Seleccionar estado"
                style="width: 100%"
              >
                <a-select-option :key="''"> Todos los estados </a-select-option>
                <a-select-option :key="0"> Todos los estados activos </a-select-option>
                <a-select-option v-for="state in status" :key="state.id">{{ state.name }}</a-select-option>
              </a-select>
            </div>
            <div class="filter-item">
              <ProfilePicker v-model:value="profile" show-all-picker />
            </div>
          </div>
        </template>
        <a-button class="more-filters-btn">
          Más filtros <span class="more-filters-badge">{{ encapsulatedFiltersCount }}</span>
        </a-button>
      </a-popover>
    </div>

    <div class="info">
      <p>
        Total de usuarios: <span class="users_number"> {{ usersCount }} </span>
      </p>

      <a-divider type="vertical" class="divider" />

      <div class="switch">
        Vista compacta
        <a-switch v-model:checked="detailedView" />
      </div>
    </div>
  </div>
</template>

<script>
import { SearchOutlined } from "@ant-design/icons-vue";
import { defineComponent, ref, computed, watch, inject, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import debounce from "lodash/debounce";

import EntityPicker from "@/app/shared/components/pickers/EntityPicker.vue";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import ProfilePicker from "@/app/shared/components/pickers/ProfilePicker.vue";
import QueuePicker from "@/app/shared/components/pickers/QueuePicker.vue";

export default defineComponent({
  components: {
    EntityPicker,
    CampaignPicker,
    SearchOutlined,
    ProfilePicker,
    QueuePicker,
  },
  setup() {
    const store = useStore();

    const user = computed(() => store.getters["getDataUser"]);
    const entity = ref(0);

    const detailedView = inject("detailedView");

    const usersCount = computed(() => store.getters["getMonitorUserList"]?.length ?? 0);

    const windowWidth = ref(window.innerWidth);

    const updateWidth = () => {
      windowWidth.value = window.innerWidth;
    };

    onMounted(() => {
      window.addEventListener("resize", updateWidth);
    });

    onUnmounted(() => {
      window.removeEventListener("resize", updateWidth);
    });

    if (user.value) {
      if (user.value.customer.type_business == 2) {
        entity.value = 1;
      }
    }

    watch(user, (newValue) => {
      if (newValue.customer.type_business == 2) {
        entity.value = 1;
      }
    });

    return {
      entity,
      user,
      detailedView,
      usersCount,
      windowWidth,
    };
  },
  data() {
    return {
      search: "",
      campaign: 0,
      queue: 0,
      pollingTimeout: null,
      isLoading: false,
      isComponentMounted: false,
      status: [],
      selected: "",
      profile: "",
    };
  },
  computed: {
    hasActiveFilters() {
      let active = this.selected !== "" || this.profile !== "";
      if (this.windowWidth < 1300) {
        active = active || this.queue !== 0;
      }
      return active;
    },
    encapsulatedFiltersCount() {
      let count = 2; // Estado and Profile
      if (this.windowWidth < 1300) count++; // Colas
      return count;
    },
  },
  sockets: {
    refreshTickets() {
      this.handleSearch();
    },
  },
  watch: {
    entity() {
      this.handleSearch();
    },
    campaign(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.queue = 0;
      }
      this.handleSearch();
    },
    queue() {
      this.handleSearch();
    },
    selected() {
      this.handleSearch();
    },
    profile() {
      this.handleSearch();
    },
  },
  mounted() {
    this.isComponentMounted = true;
    this.getStatusMonitoring();
    this.fetchData();
  },
  unmounted() {
    this.isComponentMounted = false;
    clearTimeout(this.pollingTimeout);
  },
  methods: {
    resetFilters() {
      this.selected = "";
      this.profile = "";
      if (this.windowWidth < 1300) {
        this.queue = 0;
      }
    },
    async fetchData(isAutoPoll = false) {
      if (isAutoPoll && this.isLoading) {
        this.schedulePoll();
        return;
      }
      this.isLoading = true;
      clearTimeout(this.pollingTimeout);
      try {
        const filters = {
          search: this.search,
          entity: this.entity,
          portfolio: this.campaign,
          queue: this.queue,
          state: this.selected,
          profile: this.profile,
        };
        await this.$store.dispatch("fetchMonitorUsers", filters);
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
        this.schedulePoll();
      }
    },
    schedulePoll() {
      if (!this.isComponentMounted) return;
      clearTimeout(this.pollingTimeout);
      this.pollingTimeout = setTimeout(() => {
        this.fetchData(true);
      }, 2000);
    },
    handleSearch: debounce(function () {
      this.fetchData();
    }, 300),
    async getStatusMonitoring() {
      try {
        const response = await this.$store.dispatch("fetchStatusMonitoring");
        this.status = response?.filter((status) => status.id !== 6);
        // Status id 6 is Disconnected
        console.log(this.status);
      } catch (error) {
        console.log(error);
      }
    },
  },
});
</script>

<style lang="sass" scoped>
.wrapper
  display: flex
  margin-bottom: 12px
  justify-content: space-between
  align-items: center
  gap: 8px

.controls
  // display: grid
  // grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr
  display: flex
  gap: 8px

.info
  display: flex
  gap: 12px
  font-size: 16px
  color: $gray-8

.divider
  background-color: $gray-5
  height: auto

.queue-picker-wrapper
  display: block

p
  margin-bottom: 0

.switch
  display: flex
  align-items: center
  gap: 8px

.users_number
  font-size: 20px
  font-weight: 600
  color: $gray-9

.search
  width: 100%
  height: 32px

.form__select
  height: 32px

// .form__select
//   width: 100%
//   & > :deep(.ant-select-arrow)
//     top: 30%

.ant-form-item
  margin-bottom: 0

.more-filters-btn
  display: flex
  align-items: center
  gap: 8px
  border-radius: 6px
  padding-right: 6px

.more-filters-badge
  display: flex
  align-items: center
  justify-content: center
  width: 24px
  height: 24px
  border-radius: 50%
  border: 1.5px solid #d9d9d9
  font-size: 13px
  color: #434343
  background-color: transparent
  line-height: 1

.filters-popover-content
  display: flex
  flex-direction: column
  gap: 12px
  width: 280px

.filters-header
  display: flex
  justify-content: space-between
  align-items: center

.filters-title
  color: $gray-9
  font-size: 14px

.filters-reset
  padding: 0
  height: auto
  font-size: 14px
  color: #1890ff
  &[disabled]
    color: #bfbfbf
  &:not([disabled]):hover
    color: #40a9ff

.filter-item
  display: flex
  flex-direction: column
  gap: 4px
</style>
